import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteCandidateImage,
  fetchCandidateImageObjectUrl,
  getCandidateImagesMetadata,
  uploadCandidateImage,
} from '../services/candidate-images.service';
import type { CandidateImageKind } from '../models/candidate-images.types';

interface UseCandidateImagesResult {
  avatarUrl: string | null;
  isLoading: boolean;
  busyKind: CandidateImageKind | null;
  error: unknown;
  upload: (kind: CandidateImageKind, file: File) => Promise<void>;
  remove: (kind: CandidateImageKind) => Promise<void>;
}

/**
 * Loads the candidate's avatar as an object URL (the bytes are served raw +
 * authenticated, so they can't be a plain <img src>), and exposes
 * upload/remove. The object URL is revoked on replace and unmount.
 */
export function useCandidateImages(): UseCandidateImagesResult {
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busyKind, setBusyKind] = useState<CandidateImageKind | null>(null);
  const [error, setError] = useState<unknown>(null);
  const current = useRef<string | null>(null);

  const setUrl = useCallback((url: string | null) => {
    const previous = current.current;
    if (previous && previous !== url) {
      URL.revokeObjectURL(previous);
    }
    current.current = url;
    setAvatarUrl(url);
  }, []);

  const loadAvatar = useCallback(
    async (exists: boolean) => {
      setUrl(exists ? await fetchCandidateImageObjectUrl('avatar') : null);
    },
    [setUrl],
  );

  const reload = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const meta = await getCandidateImagesMetadata();
      await loadAvatar(meta.avatar.exists);
    } catch (nextError) {
      setError(nextError);
    } finally {
      setIsLoading(false);
    }
  }, [loadAvatar]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(
    () => () => {
      if (current.current) URL.revokeObjectURL(current.current);
    },
    [],
  );

  const upload = useCallback(
    async (kind: CandidateImageKind, file: File) => {
      setBusyKind(kind);
      setError(null);
      try {
        const meta = await uploadCandidateImage(kind, file);
        await loadAvatar(meta.avatar.exists);
      } catch (nextError) {
        setError(nextError);
        throw nextError;
      } finally {
        setBusyKind(null);
      }
    },
    [loadAvatar],
  );

  const remove = useCallback(
    async (kind: CandidateImageKind) => {
      setBusyKind(kind);
      setError(null);
      try {
        await deleteCandidateImage(kind);
        setUrl(null);
      } catch (nextError) {
        setError(nextError);
        throw nextError;
      } finally {
        setBusyKind(null);
      }
    },
    [setUrl],
  );

  return { avatarUrl, isLoading, busyKind, error, upload, remove };
}
